import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft, Clock, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function Verification() {
  const navigate = useNavigate();
  const [config, setConfig] = React.useState(null);
  const [me, setMe] = React.useState(null);
  const [form, setForm] = React.useState({
    profession: "",
    license_number: "",
    license_state: "",
    license_country: "US",
    license_expiration: "",
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        const [cfg, mine] = await Promise.all([api.get("/billing/config"), api.get("/billing/me")]);
        setConfig(cfg.data);
        setMe(mine.data);
        const v = mine.data.verification || {};
        setForm((f) => ({
          ...f,
          profession: v.profession || f.profession,
          license_number: v.license_number || f.license_number,
          license_state: v.license_state || f.license_state,
          license_country: v.license_country || f.license_country,
          license_expiration: v.license_expiration || f.license_expiration,
        }));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.profession || !form.license_number || !form.license_state) {
      toast.error("Please fill profession, license number, and state.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.license_expiration) delete payload.license_expiration;
      const r = await api.post("/billing/verification/submit", payload);
      setMe((m) => ({ ...(m || {}), verification: r.data }));
      toast.success("Submitted! An admin will review shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !config || !me) {
    return <Layout><div className="text-ink-500 animate-pulse">Loading verification…</div></Layout>;
  }

  const v = me.verification || {};
  const professions = config.verification?.supported_professions || [];

  return (
    <Layout>
      <div data-testid="verification-page" className="max-w-2xl">
        <Link to="/billing" className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to billing
        </Link>
        <div className="label-overline mb-2">Professional verification</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-ink-900 mb-3">Verify your license</h1>
        <p className="text-ink-700 mb-6">
          Required before subscribing to a paid plan. Posturra is designed for licensed manual-therapy professionals.
          Review usually takes 1–2 business days.
        </p>

        <StatusBanner status={v.status} notes={v.reviewer_notes} />

        <form onSubmit={submit} className="space-y-4 mt-6">
          <Field label="Profession">
            <select
              value={form.profession}
              onChange={(e) => update("profession", e.target.value)}
              data-testid="verify-profession"
              className="w-full min-h-[44px] px-3 rounded-2xl border border-paper-200 bg-white text-ink-900"
            >
              <option value="">Select your profession…</option>
              {professions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="License number">
            <input
              type="text"
              value={form.license_number}
              onChange={(e) => update("license_number", e.target.value)}
              data-testid="verify-license-number"
              className="w-full min-h-[44px] px-3 rounded-2xl border border-paper-200 bg-white text-ink-900"
              placeholder="e.g. MA-12345"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="State / Province">
              <input
                type="text"
                value={form.license_state}
                onChange={(e) => update("license_state", e.target.value)}
                data-testid="verify-license-state"
                className="w-full min-h-[44px] px-3 rounded-2xl border border-paper-200 bg-white text-ink-900"
                placeholder="e.g. CA"
              />
            </Field>
            <Field label="Country">
              <input
                type="text"
                value={form.license_country}
                onChange={(e) => update("license_country", e.target.value)}
                data-testid="verify-license-country"
                className="w-full min-h-[44px] px-3 rounded-2xl border border-paper-200 bg-white text-ink-900"
              />
            </Field>
          </div>
          <Field label="License expiration (optional)">
            <input
              type="date"
              value={form.license_expiration}
              onChange={(e) => update("license_expiration", e.target.value)}
              data-testid="verify-license-expiration"
              className="w-full min-h-[44px] px-3 rounded-2xl border border-paper-200 bg-white text-ink-900"
            />
          </Field>
          <button
            type="submit"
            disabled={submitting}
            data-testid="verify-submit-btn"
            className="min-h-[48px] px-6 rounded-full bg-ink-900 text-white font-medium hover:bg-ink-800 inline-flex items-center gap-2"
          >
            <ShieldCheck className="w-4 h-4" />
            {v.status === "pending" ? "Re-submit" : "Submit for review"}
          </button>
        </form>
      </div>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="label-overline mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function StatusBanner({ status, notes }) {
  if (!status || status === "unverified") return null;
  const map = {
    pending: { icon: Clock, color: "text-amber-700 border-amber-500/40 bg-amber-500/5", title: "Pending review", desc: "We'll email you when a decision is made." },
    verified: { icon: CheckCircle2, color: "text-sage-700 border-sage-500/40 bg-sage-500/5", title: "Verified", desc: "You're approved to subscribe to a paid plan." },
    denied: { icon: XCircle, color: "text-terracotta-700 border-terracotta-500/40 bg-terracotta-500/5", title: "Verification denied", desc: notes || "Please contact support." },
    needs_documents: { icon: AlertTriangle, color: "text-amber-700 border-amber-500/40 bg-amber-500/5", title: "Additional documents requested", desc: notes || "An admin asked for more information." },
  };
  const cfg = map[status];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <div data-testid={`verify-status-${status}`} className={`rounded-3xl border p-4 flex items-start gap-3 ${cfg.color}`}>
      <Icon className="w-5 h-5 shrink-0 mt-0.5" />
      <div>
        <div className="font-medium">{cfg.title}</div>
        <div className="text-sm opacity-80">{cfg.desc}</div>
      </div>
    </div>
  );
}
